// The perf modes' on-screen box and their shared report slot: a status line while running, then the result as text
// plus the report's JSON with Copy and Save buttons (a phone on the LAN is plain http, so copying falls back to
// selecting and execCommand). One perf run per page: the slot is module state.

let report: Record<string, unknown> | null = null;
/** The running or finished perf mode's report (null when no perf mode runs). */
export const perfReport = (): Record<string, unknown> | null => report;
/** Set the report perfReport() returns. */
export const setPerfReport = (r: Record<string, unknown> | null): void => void (report = r);

export interface PerfOverlay {
  status: (s: string) => void;
  /** The finished tour's box (a tour report: its budget text, p95 and wall time in the headline). */
  done: (r: Record<string, unknown>) => void;
  /** The finished box: a headline, text lines, the JSON with Copy and Save buttons (`file`: the download's prefix). */
  show: (headline: string, lines: string[], r: Record<string, unknown>, file: string) => void;
}

/** The box (#perf-tour, its JSON in #perf-json), added to the page. `tier` labels the headline and the download. */
export function overlay(tier: string): PerfOverlay {
  const box = document.createElement("div");
  box.id = "perf-tour";
  box.style.cssText =
    "position:fixed;top:8px;left:8px;right:8px;max-width:760px;z-index:10;font:12px/1.4 ui-monospace,Menlo,Consolas,monospace;color:#e8ecff;background:rgba(10,12,24,.82);border:1px solid #4a5f9a;border-radius:6px;padding:8px 10px;pointer-events:auto";
  const line = document.createElement("div");
  box.append(line);
  document.body.append(box);
  return {
    status: (s) => {
      line.textContent = s;
    },
    done: (r) => {
      const b = r.budget as { text: string[]; pass: boolean };
      const t = r.timing as { frameMs: { p95: number }; tourWallSeconds: number };
      show(`perf tour done in ${t.tourWallSeconds} s · tier ${tier} · p95 ${t.frameMs.p95} ms · budget ${b.pass ? "PASS" : "FAIL"} — copy the JSON below and paste it back`, b.text, r, "perf");
    },
    show: (headline, lines, r, file) => show(headline, lines, r, file),
  };
  function show(headline: string, lines: string[], r: Record<string, unknown>, file: string): void {
    line.textContent = headline;
    const pre = document.createElement("div");
    pre.style.cssText = "white-space:pre-wrap;margin:6px 0;color:#c9d2f5";
    pre.textContent = lines.join("\n");
    const ta = document.createElement("textarea");
    ta.id = "perf-json";
    ta.readOnly = true;
    ta.value = JSON.stringify(r, null, 1);
    ta.style.cssText = "width:100%;height:38vh;box-sizing:border-box;font:11px/1.3 ui-monospace,Menlo,Consolas,monospace;background:#05060c;color:#e8ecff;border:1px solid #33406a";
    const btn = document.createElement("button");
    btn.textContent = "Copy JSON";
    btn.style.cssText = "margin-top:6px;padding:8px 14px;font:13px system-ui;background:#4a5f9a;color:#fff;border:0;border-radius:4px";
    btn.onclick = () => {
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, ta.value.length);
      const ok = (): void => void (btn.textContent = "Copied ✓");
      const legacy = (): void => void (document.execCommand("copy") ? ok() : (btn.textContent = "Select the text and copy it by hand"));
      if (navigator.clipboard?.writeText) navigator.clipboard.writeText(ta.value).then(ok, legacy);
      else legacy();
    };
    const save = document.createElement("button");
    save.textContent = "Save .json";
    save.style.cssText = btn.style.cssText + ";margin-left:8px";
    save.onclick = () => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([ta.value], { type: "application/json" }));
      a.download = `${file}-${tier}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.json`;
      a.click();
    };
    box.append(pre, ta, btn, save);
  }
}

/** Poll `isReady` every 250 ms (showing `label: waiting for <what> to load… N s`), then call `go` 0.5 s later. */
export function whenReady(label: string, what: string, ui: Pick<PerfOverlay, "status">, isReady: () => boolean, go: () => void): void {
  const bootStart = performance.now();
  const wait = (): void => {
    if (!isReady()) {
      ui.status(`${label}: waiting for ${what} to load… ${((performance.now() - bootStart) / 1000).toFixed(0)} s`);
      setTimeout(wait, 250);
      return;
    }
    setTimeout(go, 500);
  };
  wait();
}
